import React, { useEffect, useState } from "react";
import { Button, Form, notification, Select, Spin } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import B2C2BApi from "../../api-services/b2c2b-api.service";

const PAYMENT_CHANNELS = [
  { id: 1, value: "dobw",          logo: "duitnow.png",  label: "Online Banking/Wallets", bankName: "dobw_banklist",  fieldLabel: "Bank" },
  { id: 2, value: "public_bank_2", logo: "master.png",   label: "Credit/Debit Card",      bankName: "pbb_banklist",  fieldLabel: "Card Method" },
  { id: 3, value: "fpx_b2c",       logo: "fpx-new.png",  label: "B2C (Retail Banking)",   bankName: "banklist",      fieldLabel: "Bank" },
];

function fmt(currency: string, myr: number | string | undefined, foreign: number | string | undefined) {
  if (currency === "MYR") return `RM ${Number(myr ?? 0).toFixed(2)}`;
  return `${currency} ${Number(foreign ?? 0).toFixed(2)}`;
}

function SummaryRow({ label, value, valueStyle }: { label: string; value: string; valueStyle?: React.CSSProperties }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #ECECEC", fontSize: 14 }}>
      <span style={{ color: "#8c8c8c" }}>{label}</span>
      <span style={valueStyle}>{value}</span>
    </div>
  );
}

function BreakdownRow({ label, value, valueStyle }: { label: string; value: string; valueStyle?: React.CSSProperties }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 20px", borderBottom: "1px solid #ECECEC", fontSize: 14 }}>
      <span style={{ color: "#8c8c8c" }}>{label}</span>
      <span style={valueStyle}>{value}</span>
    </div>
  );
}

function DedicatedPaymentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading]                       = useState(true);
  const [submitting, setSubmitting]                 = useState(false);
  const [paymentInfo, setPaymentInfo]               = useState<any>(null);
  const [allPaymentMethods, setAllPaymentMethods]   = useState<any>({});
  const [selectedChannel, setSelectedChannel]       = useState<any>(null);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<any[]>([]);
  const [fareType, setFareType]                     = useState<"customer_full_fare" | "customer_deposit">("customer_full_fare");

  useEffect(() => {
    Promise.all([
      B2C2BApi.getPaymentInfo(Number(id)),
      B2C2BApi.getAllPaymentMethods(),
    ])
      .then(([info, methods]) => {
        setPaymentInfo(info);
        setAllPaymentMethods(methods);
        if (info?.error) {
          notification.error({ message: info.error });
        } else if (info?.allow_customer_pay_deposit && !info?.deposit_covered) {
          setFareType("customer_deposit");
        }
      })
      .catch(() => notification.error({ message: "Failed to load payment details." }))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChannelSelect = (channel: any) => {
    form.setFieldValue("payment_method", undefined);
    setSelectedChannel(channel);
    const options = (allPaymentMethods?.[channel.bankName] || []).map((item: any) => {
      const [label, value] = item;
      return {
        value,
        label,
        disabled: typeof label === "string" && label.includes("(Offline)"),
      };
    });
    setPaymentMethodOptions(options);
  };

  const handleSubmit = (values: any) => {
    if (!selectedChannel) {
      notification.warning({ message: "Please select a payment method." });
      return;
    }
    setSubmitting(true);
    B2C2BApi.createPayment(Number(id), {
      payment: {
        gateway: selectedChannel.value,
        method: values.payment_method || "",
        bank_type: "",
        redirect_url: "",
      },
      fare_type_selected: fareType,
    })
      .then((response: any) => {
        if (response.error) {
          notification.error({ message: response.error });
        } else if (response.redirect_to) {
          window.location.href = response.redirect_to;
        } else {
          notification.error({ message: "No redirect URL was provided by the server." });
        }
      })
      .catch(() => notification.error({ message: "Payment could not be processed. Please try again." }))
      .finally(() => setSubmitting(false));
  };

  if (!paymentInfo && !loading) return null;

  const cur = paymentInfo?.currency ?? "MYR";

  const totalPrice             = paymentInfo?.total_price ?? 0;
  const deposit                = paymentInfo?.deposit ?? 0;
  const currDeposit            = paymentInfo?.currency_deposit ?? 0;
  const totalQuantity          = paymentInfo?.total_quantity ?? 0;
  const currBalanceAmount      = paymentInfo?.currency_balance_amount ?? 0;
  const balanceAmount          = paymentInfo?.balance_amount ?? 0;

  const displayTotal           = fmt(cur, totalPrice, totalPrice);
  const displayDeposit         = fmt(cur, deposit, currDeposit);
  const depositPerPax          = totalQuantity > 0 ? deposit / totalQuantity : 0;
  const currDepositPerPax      = totalQuantity > 0 ? currDeposit / totalQuantity : 0;
  const displayDepositPerPax   = fmt(cur, depositPerPax, currDepositPerPax);
  const remainingAfterDeposit  = totalPrice - deposit;
  const displayRemaining       = fmt(cur, remainingAfterDeposit, currBalanceAmount);

  const payingNow              = fareType === "customer_deposit" ? deposit : balanceAmount;
  const currPayingNow          = fareType === "customer_deposit" ? currDeposit : currBalanceAmount;
  const displayPayingNow       = fmt(cur, payingNow, currPayingNow);

  const ccRate = paymentInfo?.cc_rate ? `${(paymentInfo.cc_rate * 100).toFixed(1)}%` : null;

  const showDepositToggle = paymentInfo?.allow_customer_pay_deposit && !paymentInfo?.deposit_covered;

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: "12px 16px",
    background: "none",
    border: "none",
    borderBottom: active ? "2px solid #DC1A22" : "2px solid transparent",
    color: active ? "#DC1A22" : "#8c8c8c",
    fontWeight: active ? 700 : 400,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "Poppins, sans-serif",
    letterSpacing: "0.05em",
  });

  return (
    <div id="dedicated-payment-page" className="bookings-page b2c2b-section">
      {submitting && <Spin spinning fullscreen />}

      <div className="bookings-page_header" style={{ marginBottom: 4 }}>
        <h1>Complete your payment</h1>
      </div>
      {paymentInfo && (
        <p style={{ marginBottom: 10, color: "#8c8c8c" }}>
          Order <Link to={`/web/bookings/${id}`} style={{ color: "#DC1A22", fontWeight: 600 }}>{paymentInfo.order_no}</Link>
        </p>
      )}

      {loading ? <Spin spinning /> : paymentInfo?.error ? null : (
      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
        {/* LEFT — main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>

            {/* Booking summary */}
            <div className="b2c2b-section_panel" style={{ marginBottom: 24, overflow: "hidden" }}>
              <div className="b2c2b-section_panel-header">
                <label>Booking summary</label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", padding: "0 20px 20px", gap: "0 24px" }}>
                <div style={{ borderBottom: "1px solid #ECECEC", padding: "12px 0" }}>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4, textTransform: "capitalize" }}>Tour</div>
                  <div style={{ fontSize: 14, color: "#DC1A22", fontWeight: 500 }}>{paymentInfo.package_name}</div>
                </div>
                <div style={{ borderBottom: "1px solid #ECECEC", padding: "12px 0" }}>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4, textTransform: "capitalize" }}>Tour code</div>
                  <div style={{ fontSize: 14 }}>{paymentInfo.code}</div>
                </div>
                <div style={{ borderBottom: "1px solid #ECECEC", padding: "12px 0" }}>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4, textTransform: "capitalize" }}>Departure date</div>
                  <div style={{ fontSize: 14 }}>{paymentInfo.departure_date || "—"}</div>
                </div>
                <div style={{ borderBottom: "1px solid #ECECEC", padding: "12px 0" }}>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4, textTransform: "capitalize" }}>Destination</div>
                  <div style={{ fontSize: 14 }}>{paymentInfo.destination}</div>
                </div>
                <div style={{ padding: "12px 0" }}>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4, textTransform: "capitalize" }}>Traveler</div>
                  <div style={{ fontSize: 14 }}>{paymentInfo.traveler || "—"}</div>
                </div>
                <div style={{ padding: "12px 0" }}>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4, textTransform: "capitalize" }}>Pax</div>
                  <div style={{ fontSize: 14 }}>{paymentInfo.total_quantity} Pax</div>
                </div>
              </div>
            </div>

            {/* Payment terms */}
            {paymentInfo.allow_customer_pay_deposit && (
              <div className="b2c2b-section_panel" style={{ marginBottom: 24, overflow: "hidden" }}>
                <div className="b2c2b-section_panel-header">
                  <label>Payment terms</label>
                </div>

                {showDepositToggle && (
                  <div style={{ display: "flex", borderBottom: "1px solid #ECECEC" }}>
                    <button
                      type="button"
                      style={tabStyle(fareType === "customer_deposit")}
                      onClick={() => setFareType("customer_deposit")}
                    >
                      Pay deposit
                    </button>
                    <button
                      type="button"
                      style={tabStyle(fareType === "customer_full_fare")}
                      onClick={() => setFareType("customer_full_fare")}
                    >
                      Pay in full
                    </button>
                  </div>
                )}

                {fareType === "customer_deposit" && showDepositToggle ? (
                  <>
                    <BreakdownRow label="Tour price" value={displayTotal} />
                    <BreakdownRow
                      label="Deposit amount"
                      value={`${displayDeposit} (${displayDepositPerPax} × ${paymentInfo.total_quantity} pax)`}
                    />
                    <BreakdownRow
                      label="Remaining balance"
                      value={displayRemaining}
                      valueStyle={{ color: "#DC1A22", fontWeight: 600 }}
                    />
                    {paymentInfo.payment_deadline && (
                      <div style={{ margin: "12px 20px 20px", background: "#FFFBE6", border: "1px solid #FFE58F", borderRadius: 8, padding: "10px 16px", fontSize: 14 }}>
                        Final payment of <strong>{displayRemaining}</strong> is due before <strong>{paymentInfo.payment_deadline}</strong>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <BreakdownRow label="Tour price" value={displayTotal} />
                    <BreakdownRow
                      label="Remaining balance after payment"
                      value={`${fmt(cur, 0, 0)} — fully paid`}
                      valueStyle={{ color: "#52C41A", fontWeight: 600 }}
                    />
                  </>
                )}
              </div>
            )}

            {/* Payment method */}
            <div className="b2c2b-section_panel" style={{ marginBottom: 24, overflow: "hidden" }}>
              <div className="b2c2b-section_panel-header">
                <label>Select payment method</label>
              </div>
              <div className="payment-gateway_channels">
                {PAYMENT_CHANNELS.map((channel) => {
                  const available =
                    channel.value === "dobw"    ? paymentInfo.dobw_available :
                    channel.value === "fpx_b2c" ? paymentInfo.fpxb2c_available :
                    true;
                  return (
                    <Button
                      key={channel.id}
                      type="default"
                      variant="outlined"
                      disabled={!available}
                      onClick={() => available && handleChannelSelect(channel)}
                      className={`payment-gateway_channels-box ${selectedChannel?.value === channel.value ? "selected" : ""}`}
                    >
                      <img src={`/iceholidays-assets/images/payment/${channel.logo}`} alt={channel.label} />
                      <span>{channel.label}</span>
                    </Button>
                  );
                })}
              </div>

              {paymentMethodOptions.length > 0 && (
                <Form.Item
                  label={selectedChannel?.fieldLabel}
                  name="payment_method"
                  rules={[{ required: true }]}
                  style={{ padding: "0 20px", marginTop: 16 }}
                >
                  <Select options={paymentMethodOptions} placeholder="Select..." />
                </Form.Item>
              )}

              {selectedChannel?.value === "public_bank_2" && ccRate && (
                <p style={{ padding: "0 20px", color: "#888", fontSize: 13 }}>
                  A service fee of {ccRate} applies for credit/debit card payments.
                </p>
              )}
              <div style={{ height: 20 }} />
            </div>

            <div style={{ marginBottom: 32 }}>
              <Button onClick={() => navigate(-1)}>← Back</Button>
            </div>
          </Form>
        </div>

        {/* RIGHT — summary sidebar */}
        <div style={{ width: 280, flexShrink: 0, position: "sticky", top: 24 }}>
          <div style={{ background: "#fff", borderLeft: "4px solid #DC1A22", borderRadius: 12, padding: 20, boxShadow: "0px 5px 20px 0px rgba(0,0,0,0.15)" }}>
            <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>Summary</h3>

            <SummaryRow label="Package" value={paymentInfo.code} />
            <SummaryRow label="Tour price" value={displayTotal} />

            {fareType === "customer_deposit" && showDepositToggle ? (
              <>
                <SummaryRow
                  label="Remaining balance"
                  value={displayRemaining}
                  valueStyle={{ color: "#DC1A22", fontWeight: 600 }}
                />
                {paymentInfo.payment_deadline && (
                  <SummaryRow label="Final payment due" value={paymentInfo.payment_deadline} />
                )}
              </>
            ) : (
              <SummaryRow
                label="Balance after payment"
                value={fmt(cur, 0, 0)}
                valueStyle={{ color: "#52C41A", fontWeight: 600 }}
              />
            )}

            <div style={{ marginTop: 10, marginBottom: 4, fontSize: 13, color: "#8c8c8c" }}>Paying now</div>
            <div style={{ color: "#DC1A22", fontSize: 24, fontWeight: 700, marginBottom: 16 }}>
              {displayPayingNow}
            </div>

            <Button
              color="primary"
              variant="solid"
              block
              loading={submitting}
              onClick={() => form.submit()}
            >
              Pay now
            </Button>

            <p style={{ fontSize: 12, color: "#888", marginTop: 12, lineHeight: 1.5 }}>
              Your payment is encrypted and processed securely. We do not store your card details.
            </p>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default DedicatedPaymentPage;
